import { useState, type ReactNode } from 'react'
import {
  faChevronDown,
  faChevronUp,
  faSeedling,
} from '@fortawesome/pro-solid-svg-icons'
import { Button, Icon, Text, Textarea, Toggle } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { BOOKKEEPER_NOTE_MAX_LENGTH } from '../../lib/constants'
import { MerchantLogo } from '@/components/MerchantLogo'
import { BankDetailLines } from './BankDetailLines'
import {
  UNSURE_VALUE,
  type SeparationGroup,
  type SeparationRowPick,
} from './separation'
import {
  choicesFor,
  peerAnswersOf,
  type AnswerChoice,
} from './separationChoices'

const offersUnsure = (group: SeparationGroup): boolean =>
  group.allowedAnswers.some((answer) => answer.value === UNSURE_VALUE)

const offersSplit = (group: SeparationGroup): boolean =>
  group.transactionCount > 1 &&
  group.moreRowCount === 0 &&
  peerAnswersOf(group).length > 0

const VERDICTS: Record<string, string> = {
  personal: 'Reads as personal',
  business: 'Reads as business',
}

const verdictFor = (group: SeparationGroup): string | null => {
  if (group.kind === 'money_movement') return 'Money moving, not spending'
  return group.suggestion ? (VERDICTS[group.suggestion] ?? null) : null
}

const PREVIEW_ROW_COUNT = 3

const listToggleLabel = (open: boolean, count: number): string =>
  open ? 'Show fewer' : `Show all ${count}`

const describeAccounts = (group: SeparationGroup): string | null => {
  const [account] = group.accounts
  if (!account) return null
  const others = group.accountCount - 1
  return others > 0 ? `${account} + ${others} more` : account
}

const describeGroup = (group: SeparationGroup): string => {
  const count = `${group.transactionCount} transaction${group.transactionCount === 1 ? '' : 's'}`
  const accounts = describeAccounts(group)
  return accounts ? `${accounts} · ${count}` : count
}

const Shell = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-3 rounded-[16px] border border-neutral-200 bg-neutral-0 p-4 shadow-sm">
    {children}
  </div>
)

const NOTE_PLACEHOLDER = 'Anything that helps place these charges'

// Stacks under 28rem: the money movement labels overrun a narrow panel.
const BUTTON_ROW = 'flex flex-col gap-2 @md:flex-row @md:flex-wrap'
const ROW_BUTTON = 'w-full @md:w-auto'

/** Every row starts on the group's suggestion, so only the exceptions need a click. */
const suggestedPicks = (group: SeparationGroup): Record<number, string> => {
  const { suggestion } = group
  if (
    !suggestion ||
    !peerAnswersOf(group).some((answer) => answer.value === suggestion)
  )
    return {}
  return Object.fromEntries(group.rows.map((_, index) => [index, suggestion]))
}

interface RowChoicesProps {
  label: string
  choices: AnswerChoice[]
  disabled: boolean
  pick: string | undefined
  onPick: (value: string) => void
}

const RowChoices = ({
  label,
  choices,
  disabled,
  pick,
  onPick,
}: RowChoicesProps) => (
  <div
    role="group"
    aria-label={label}
    className="flex flex-wrap gap-2 sm:justify-end"
  >
    {choices.map((choice) => (
      <Toggle
        key={choice.key}
        variant="outline"
        size="sm"
        className="body-sm"
        checked={pick === choice.value}
        disabled={disabled}
        onCheckedChange={() => onPick(choice.value)}
      >
        {choice.label}
      </Toggle>
    ))}
  </div>
)

interface SeparationGroupCardProps {
  group: SeparationGroup
  progress?: { position: number; total: number } | null
  disabled: boolean
  saving: boolean
  saveFailed: boolean
  onAnswer: (value: string) => void
  onHandOff: (note: string) => void
  onSaveSplit: (picks: SeparationRowPick[]) => void
}

export const SeparationGroupCard = ({
  group,
  progress,
  disabled,
  saving,
  saveFailed,
  onAnswer,
  onHandOff,
  onSaveSplit,
}: SeparationGroupCardProps) => {
  const [listOpen, setListOpen] = useState(false)
  const [splitting, setSplitting] = useState(false)
  const [handingOff, setHandingOff] = useState(false)
  const [picks, setPicks] = useState<Record<number, string>>(() =>
    suggestedPicks(group)
  )
  const [note, setNote] = useState('')
  const verdict = verdictFor(group)
  const hasEvidence = verdict !== null || group.reason !== null
  const allPicked = group.rows.every((_, index) => picks[index] !== undefined)
  const locked = disabled || saving
  const showingAll = listOpen || splitting
  const visibleRows = showingAll
    ? group.rows
    : group.rows.slice(0, PREVIEW_ROW_COUNT)
  const hasHiddenRows = group.transactionCount > PREVIEW_ROW_COUNT

  const startSplit = () => {
    setSplitting(true)
    setListOpen(true)
  }

  const cancelSplit = () => {
    setSplitting(false)
    setPicks(suggestedPicks(group))
  }

  const pickRow = (index: number, value: string) =>
    setPicks((current) => ({ ...current, [index]: value }))

  const cancelHandOff = () => {
    setHandingOff(false)
    setNote('')
  }

  const saveSplit = () =>
    onSaveSplit(
      group.rows.flatMap((row, index) => {
        const value = picks[index]
        return value ? [{ transactionIds: row.transactionIds, value }] : []
      })
    )

  const failure = saveFailed && (
    <Text variant={TypographyStyles.BODY_XS} color={TextColors.DANGER}>
      {"Couldn't save that. Try again."}
    </Text>
  )

  const handOffControls = (
    <div className="flex flex-col gap-2">
      <Text variant={TypographyStyles.LABEL_SM} color={TextColors.HEADER}>
        {"Tell Heard's bookkeepers what you know about these."}
      </Text>
      <Textarea
        aria-label="Note for the bookkeeper"
        placeholder={NOTE_PLACEHOLDER}
        className="body-sm"
        rows={3}
        maxLength={BOOKKEEPER_NOTE_MAX_LENGTH}
        value={note}
        disabled={locked}
        onChange={(event) => setNote(event.target.value)}
      />
      <div className={BUTTON_ROW}>
        <Button
          size="sm"
          variant="primary"
          className={ROW_BUTTON}
          disabled={locked || note.trim() === ''}
          loading={saving}
          onClick={() => onHandOff(note.trim())}
        >
          Send to a bookkeeper
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className={ROW_BUTTON}
          disabled={saving}
          onClick={cancelHandOff}
        >
          Cancel
        </Button>
      </div>
    </div>
  )

  const choiceButton = (choice: AnswerChoice) => (
    <Button
      key={choice.key}
      size="sm"
      variant={choice.value === group.suggestion ? 'primary' : 'secondary'}
      className={ROW_BUTTON}
      disabled={locked}
      onClick={() => onAnswer(choice.value)}
    >
      {choice.label}
    </Button>
  )

  const answerChoices = (
    <div className={BUTTON_ROW}>
      {choicesFor(group, { countLabels: true }).map(choiceButton)}
      {offersSplit(group) && (
        <Button
          size="sm"
          variant="secondary"
          className={ROW_BUTTON}
          disabled={locked}
          onClick={startSplit}
        >
          {"It's a mix"}
        </Button>
      )}
      {offersUnsure(group) && (
        <Button
          size="sm"
          variant="ghost"
          className={ROW_BUTTON}
          disabled={locked}
          onClick={() => setHandingOff(true)}
        >
          {"I'm not sure"}
        </Button>
      )}
    </div>
  )

  const splitButtons = (
    <div className={BUTTON_ROW}>
      <Button
        size="sm"
        variant="primary"
        className={ROW_BUTTON}
        disabled={locked || !allPicked}
        loading={saving}
        onClick={saveSplit}
      >
        Save
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className={ROW_BUTTON}
        disabled={saving}
        onClick={cancelSplit}
      >
        Cancel
      </Button>
    </div>
  )

  const currentControls = () => {
    if (handingOff) return handOffControls
    if (splitting) return splitButtons
    return answerChoices
  }

  return (
    <div className="@container">
      <Shell>
        <div className="flex items-start gap-3">
          <MerchantLogo name={group.groupName} logoUrl={group.logoUrl} />
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {progress && (
              <Text variant={TypographyStyles.EYEBROW} color={TextColors.MUTED}>
                {progress.position} of {progress.total}
              </Text>
            )}
            <Text
              variant={TypographyStyles.LABEL_MD}
              color={TextColors.HEADER}
              className="[overflow-wrap:anywhere]"
            >
              {group.groupName}
            </Text>
            <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
              {describeGroup(group)}
            </Text>
          </div>
          <Text
            variant={TypographyStyles.LABEL_MD}
            color={TextColors.HEADER}
            className="shrink-0 tabular-nums"
          >
            {group.amountDisplay}
          </Text>
        </div>

        {group.rows.length > 0 && (
          <div
            className={mtc(
              'flex flex-col',
              !splitting && 'overflow-hidden rounded-[12px] bg-neutral-25'
            )}
          >
            <ul aria-label="Transactions">
              {visibleRows.map((row, index) => (
                <li
                  key={`${row.dateDisplay}-${index}`}
                  className={mtc(
                    'grid grid-cols-1 gap-2 border-t py-2 first:border-t-0',
                    splitting
                      ? 'border-neutral-50 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-x-3'
                      : 'border-neutral-0 px-3'
                  )}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <Text
                      variant={TypographyStyles.BODY_XS}
                      color={TextColors.MUTED}
                      className="w-[5.5rem] shrink-0 whitespace-nowrap"
                    >
                      {row.dateDisplay}
                    </Text>
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      className="min-w-0 flex-1 [overflow-wrap:anywhere]"
                    >
                      <BankDetailLines
                        details={row.details}
                        description={row.description}
                      />
                    </Text>
                    <Text
                      variant={TypographyStyles.BODY_SM}
                      textAlign="right"
                      className="shrink-0 tabular-nums"
                    >
                      {row.amountDisplay}
                    </Text>
                  </div>
                  {splitting && (
                    <RowChoices
                      label={`Answer for ${row.dateDisplay}`}
                      choices={choicesFor(group, { countLabels: false })}
                      disabled={locked}
                      pick={picks[index]}
                      onPick={(value) => pickRow(index, value)}
                    />
                  )}
                </li>
              ))}
              {showingAll && group.moreRowCount > 0 && (
                <li
                  className={mtc(
                    'border-t py-3 text-xs text-neutral-500',
                    splitting ? 'border-neutral-50' : 'border-neutral-0 px-3'
                  )}
                >
                  Plus {group.moreRowCount} more
                </li>
              )}
            </ul>
            {!splitting && hasHiddenRows && (
              <button
                type="button"
                className="flex w-full items-center gap-1 border-t border-neutral-0 p-3 text-xs font-medium text-green-100"
                aria-expanded={listOpen}
                onClick={() => setListOpen((open) => !open)}
              >
                {listToggleLabel(listOpen, group.transactionCount)}
                <Icon
                  icon={listOpen ? faChevronUp : faChevronDown}
                  className="text-[10px]"
                />
              </button>
            )}
          </div>
        )}

        {hasEvidence && !splitting && (
          <div className="flex gap-2 rounded-[8px] bg-moss-0 p-3">
            <Icon icon={faSeedling} className="mt-0.5 text-xs text-moss-200" />
            <div className="flex flex-col gap-1">
              {verdict && (
                <Text
                  variant={TypographyStyles.LABEL_XS}
                  color={TextColors.MOSS}
                >
                  {verdict}
                </Text>
              )}
              {group.reason && (
                <Text
                  variant={TypographyStyles.BODY_XS}
                  className="text-neutral-600"
                >
                  {group.reason}
                </Text>
              )}
            </div>
          </div>
        )}

        {failure}
        {currentControls()}
      </Shell>
    </div>
  )
}
